import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Brightness1Icon from '@mui/icons-material/Brightness1';
import DeleteIconOutlined from '@mui/icons-material/DeleteOutlined';
import EditIconOutlined from '@mui/icons-material/EditOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import LayersIcon from '@mui/icons-material/Layers';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { CalendarEvent } from '@src/types/Events';
import moment from 'moment';
import { Dispatch, SetStateAction } from 'react';
import { eventPopoverStyles } from './styles';

interface EventPopoverProps {
  anchorEl: HTMLElement | null;
  onEdit: () => void;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  activeEvent: CalendarEvent | null;
  currentTZ: string;
  stackName: string;
}

const formatRecurrence = (rrule: CalendarEvent['rrule']): string => {
  if (!rrule) {
    return '';
  }

  const { freq, interval = 1, byweekday, until } = rrule;

  // Format frequency with interval
  let result = interval > 1 ? `Every ${interval} ` : '';
  result += freq.toLowerCase();
  if (interval > 1) {
    result += 's';
  }

  // Add weekday information if present
  if (byweekday && Array.isArray(byweekday) && byweekday.length > 0) {
    const dayMap: { [key: string]: string } = {
      su: 'Sun',
      mo: 'Mon',
      tu: 'Tue',
      we: 'Wed',
      th: 'Thu',
      fr: 'Fri',
      sa: 'Sat',
    };
    const days = byweekday.map((d) => dayMap[d] || d).join(', ');
    result += ` on ${days}`;
  }

  // Add end condition
  if (until) {
    result += ` until ${moment(until).format('MMM DD, YYYY')}`;
  }

  return result.charAt(0).toUpperCase() + result.slice(1);
};

export default function EventPopover({
  anchorEl,
  onEdit,
  onClose,
  setOpen,
  activeEvent,
  currentTZ,
  stackName,
}: EventPopoverProps) {
  const theme = useTheme();
  const isPopoverOpen = Boolean(anchorEl);
  const id = isPopoverOpen ? 'simple-popover' : undefined;

  return (
    <Popover
      open={isPopoverOpen}
      id={id}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Card sx={eventPopoverStyles.card}>
        <CardHeader
          avatar={
            <Brightness1Icon sx={{ color: activeEvent?.backgroundColor }} />
          }
          action={
            <>
              <Tooltip title='Edit event'>
                <IconButton
                  onClick={onEdit}
                  color='primary'
                  aria-label='Edit event'
                >
                  <EditIconOutlined color='primary' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Delete event'>
                <IconButton
                  onClick={() => setOpen(true)}
                  color='error'
                  aria-label='Delete event'
                >
                  <DeleteIconOutlined color='error' sx={{ opacity: 0.8 }} />
                </IconButton>
              </Tooltip>
            </>
          }
          title={activeEvent?.title}
          sx={eventPopoverStyles.cardHeader(theme)}
        />
        <CardContent sx={eventPopoverStyles.cardContent(theme)}>
          <Stack>
            <Stack
              direction='row'
              spacing={1}
              sx={eventPopoverStyles.cardSection}
            >
              <Box flex={1}>
                <AccessAlarmIcon sx={eventPopoverStyles.icon} />
                {moment
                  .utc(activeEvent?.start)
                  .tz(currentTZ)
                  .format('DD MMM, YYYY')}
              </Box>
              <Box flex={1}>
                {moment.utc(activeEvent?.start).tz(currentTZ).format('h:mmA')} -{' '}
                {moment.utc(activeEvent?.end).tz(currentTZ).format('h:mmA')}
              </Box>
            </Stack>
            {activeEvent?.rrule && (
              <Stack
                direction='row'
                spacing={1}
                sx={eventPopoverStyles.cardSection}
              >
                <Box flex={1}>
                  <EventRepeatIcon sx={eventPopoverStyles.icon} />
                  <span>Recurrence</span>
                </Box>
                <Box flex={1}>{formatRecurrence(activeEvent.rrule)}</Box>
              </Stack>
            )}
            <Stack
              direction='row'
              spacing={1}
              sx={eventPopoverStyles.cardSection}
            >
              <Box flex={1}>
                <LayersIcon sx={eventPopoverStyles.icon} />
                <span>Stack</span>
              </Box>
              <Box sx={eventPopoverStyles.stackNameBox}>{stackName}</Box>
            </Stack>
            <Divider sx={eventPopoverStyles.divider(theme)} />
            <Stack
              direction='row'
              spacing={1}
              sx={eventPopoverStyles.cardSection}
            >
              <Box flex={1}>
                <ArrowForwardIcon sx={eventPopoverStyles.icon} />
                <span>Start Command</span>
              </Box>
              <Tooltip
                title={activeEvent?.startCommands?.join(', ') || 'No commands'}
                placement='top'
              >
                <Box sx={eventPopoverStyles.commandBox}>
                  {activeEvent?.startCommands &&
                  activeEvent.startCommands.length > 0 ? (
                    activeEvent.startCommands.join(', ')
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      None
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            </Stack>
            <Stack
              direction='row'
              spacing={1}
              sx={eventPopoverStyles.cardSection}
            >
              <Box flex={1}>
                <ArrowBackIcon sx={eventPopoverStyles.icon} />
                <span>Stop Command</span>
              </Box>
              <Tooltip
                title={activeEvent?.stopCommands?.join(', ') || 'No commands'}
                placement='top'
              >
                <Box sx={eventPopoverStyles.commandBox}>
                  {activeEvent?.stopCommands &&
                  activeEvent.stopCommands.length > 0 ? (
                    activeEvent.stopCommands.join(', ')
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      None
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Popover>
  );
}
